import { Component, type ErrorInfo, type ReactNode } from "react";
import { COPY, fill } from "./copy.js";
import { Creature } from "./creature.js";

/**
 * The last net under a render that throws.
 *
 * **A React component that throws while rendering unmounts the whole tree**, and what is left is a
 * blank page with a stack trace in a console nobody is looking at. That is the worst failure this app
 * can have: a teacher in front of a class, or a five-year-old, gets a white screen and no way forward.
 * The error itself is a bug to fix; showing nothing is a second bug on top of it.
 *
 * A class component because that is the only thing React lets catch a render error — there is no hook
 * for it, and this is the one place in the codebase where that idiom is worth keeping.
 *
 * **Reloading is offered rather than "retry".** Re-rendering the same tree with the same state
 * reproduces the same throw, so a retry button would look like it did something and change nothing. A
 * reload discards the state that caused it, and everything this app holds is either on the server or
 * in the outbox, so nothing is lost by starting again.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { message: string | null }
> {
  override state: { message: string | null } = { message: null };

  static getDerivedStateFromError(error: unknown): { message: string } {
    return { message: error instanceof Error ? error.message : String(error) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Logged rather than sent anywhere. There is no error-reporting endpoint and inventing one here
    // would ship a child's screen contents to a third party without anybody deciding to.
    console.error("render failed", error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.message === null) return this.props.children;

    return (
      <div className="grid h-full place-items-center gap-md p-xl text-center">
        <Creature kind="cat" accent="watermelon" size={88} />
        <p className="text-body text-ink">
          {fill(COPY.child.failed, { message: this.state.message })}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="min-h-touch rounded-pill bg-grape px-xl py-md text-heading text-card shadow-tint-grape"
        >
          {COPY.child.reload}
        </button>
      </div>
    );
  }
}
